import axiosInstance from '../axiosConfig';
import {
  Pro,
  ProBooking,
  ProBookingFormData,
  ProAvailability,
  ProSessionType,
  ProAvailabilitySchedule,
  ProsResponse,
  ProResponse,
  ProBookingResponse,
  ProBookingsResponse,
  ProAvailabilityResponse,
  ProSessionTypesResponse,
} from '../../types';

/**
 * Pro Booking Service
 * Handles all Pro Booking-related API calls
 */
class ProBookingService {
  private baseUrl = '/pros';
  private bookingsUrl = '/pro-bookings';
  private sessionTypesUrl = '/session-types';

  // ==========================================
  // Pro Listing Endpoints
  // ==========================================

  /**
   * Get all tennis/pickleball pros
   * @returns List of pros
   */
  async getAllPros(): Promise<Pro[]> {
    const response = await axiosInstance.get<ProsResponse>(`${this.baseUrl}`);
    return response.data.data;
  }

  /**
   * Get pro by ID with session types and availability
   * @param proId - Pro (Staff) ID
   * @returns Pro details
   */
  async getProById(proId: string): Promise<Pro> {
    const response = await axiosInstance.get<ProResponse>(`${this.baseUrl}/${proId}`);
    return response.data.data;
  }

  // ==========================================
  // Availability Endpoints
  // ==========================================

  /**
   * Get pro's availability schedule
   * @param proId - Pro ID
   * @param date - Optional specific date (YYYY-MM-DD)
   * @returns Availability schedule with slots
   */
  async getProAvailability(proId: string, date?: string): Promise<ProAvailabilitySchedule> {
    const params: any = {};
    if (date) params.date = date;

    const response = await axiosInstance.get<ProAvailabilityResponse>(
      `${this.baseUrl}/${proId}/availability`,
      { params }
    );
    return response.data.data;
  }

  /**
   * Update current pro's availability (Staff only)
   * @param availability - Array of availability slots
   * @returns Updated availability
   */
  async updateMyAvailability(availability: Partial<ProAvailability>[]): Promise<ProAvailability[]> {
    const response = await axiosInstance.put<{ success: boolean; data: ProAvailability[] }>(
      `${this.baseUrl}/me/availability`,
      availability
    );
    return response.data.data;
  }

  // ==========================================
  // Session Type Endpoints
  // ==========================================

  /**
   * Get session types offered by a pro
   * @param proId - Pro ID
   * @returns List of session types
   */
  async getProSessionTypes(proId: string): Promise<ProSessionType[]> {
    const response = await axiosInstance.get<ProSessionTypesResponse>(
      `${this.baseUrl}/${proId}/session-types`
    );
    return response.data.data;
  }

  /**
   * Create a new session type (Staff only)
   * @param data - Session type data
   * @returns Created session type
   */
  async createSessionType(data: Partial<ProSessionType>): Promise<ProSessionType> {
    const response = await axiosInstance.post<{ success: boolean; data: ProSessionType }>(
      `${this.baseUrl}/me/session-types`,
      data
    );
    return response.data.data;
  }

  /**
   * Update a session type (Staff only)
   * @param sessionTypeId - Session type ID
   * @param data - Updated session type data
   * @returns Updated session type
   */
  async updateSessionType(sessionTypeId: string, data: Partial<ProSessionType>): Promise<ProSessionType> {
    const response = await axiosInstance.put<{ success: boolean; data: ProSessionType }>(
      `${this.sessionTypesUrl}/${sessionTypeId}`,
      data
    );
    return response.data.data;
  }

  /**
   * Delete a session type (Staff only)
   * @param sessionTypeId - Session type ID
   */
  async deleteSessionType(sessionTypeId: string): Promise<void> {
    await axiosInstance.delete(`${this.sessionTypesUrl}/${sessionTypeId}`);
  }

  // ==========================================
  // Admin Endpoints
  // ==========================================

  /**
   * Create a session type for any pro (Admin only)
   * @param proId - Pro ID
   * @param data - Session type data
   * @returns Created session type
   */
  async adminCreateSessionType(proId: string, data: Partial<ProSessionType>): Promise<ProSessionType> {
    const response = await axiosInstance.post<{ success: boolean; data: ProSessionType }>(
      `/admin/pros/${proId}/session-types`,
      data
    );
    return response.data.data;
  }

  /**
   * Update availability for any pro (Admin only)
   * @param proId - Pro ID
   * @param availability - Availability slots
   * @returns Updated availability
   */
  async adminUpdateAvailability(proId: string, availability: Partial<ProAvailability>[]): Promise<ProAvailability[]> {
    const response = await axiosInstance.put<{ success: boolean; data: ProAvailability[] }>(
      `/admin/pros/${proId}/availability`,
      availability
    );
    return response.data.data;
  }

  // ==========================================
  // Booking Endpoints
  // ==========================================

  /**
   * Create a new pro booking
   * @param data - Booking form data
   * @returns Created booking
   */
  async createBooking(data: ProBookingFormData): Promise<ProBooking> {
    const response = await axiosInstance.post<ProBookingResponse>(
      `${this.bookingsUrl}`,
      data
    );
    return response.data.data;
  }

  /**
   * Get booking by ID
   * @param bookingId - Booking ID
   * @returns Booking details
   */
  async getBookingById(bookingId: string): Promise<ProBooking> {
    const response = await axiosInstance.get<ProBookingResponse>(
      `${this.bookingsUrl}/${bookingId}`
    );
    return response.data.data;
  }

  /**
   * Get current user's pro bookings
   * @param includePast - Include past bookings
   * @returns List of bookings
   */
  async getMyBookings(includePast: boolean = false): Promise<ProBooking[]> {
    const response = await axiosInstance.get<ProBookingsResponse>(
      `${this.bookingsUrl}/my`,
      { params: { include_past: includePast ? 'true' : 'false' } }
    );
    return response.data.data;
  }

  /**
   * Get current pro's bookings (Staff only)
   * @param date - Optional date filter (YYYY-MM-DD)
   * @returns List of bookings
   */
  async getMyBookingsAsPro(date?: string): Promise<ProBooking[]> {
    const params: any = {};
    if (date) params.date = date;

    const response = await axiosInstance.get<ProBookingsResponse>(
      `${this.baseUrl}/me/bookings`,
      { params }
    );
    return response.data.data;
  }

  /**
   * Cancel a booking
   * @param bookingId - Booking ID
   * @param reason - Optional cancellation reason
   * @returns Cancelled booking
   */
  async cancelBooking(bookingId: string, reason?: string): Promise<ProBooking> {
    const response = await axiosInstance.delete<ProBookingResponse>(
      `${this.bookingsUrl}/${bookingId}`,
      { data: { reason: reason || null } }
    );
    return response.data.data;
  }

  // ==========================================
  // Helper Methods
  // ==========================================

  /**
   * Format price for display
   * @param price - Price in decimal
   * @returns Formatted price string
   */
  formatPrice(price: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  }

  /**
   * Format time for display
   * @param isoString - ISO date string
   * @returns Formatted time string (e.g., "10:30 AM")
   */
  formatTime(isoString: string): string {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  /**
   * Format date for display
   * @param isoString - ISO date string
   * @returns Formatted date string (e.g., "Mon, Jan 15")
   */
  formatDate(isoString: string): string {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  }

  /**
   * Get duration label
   * @param minutes - Duration in minutes
   * @returns Human-readable duration
   */
  getDurationLabel(minutes: number): string {
    if (minutes === 30) return '30 min';
    if (minutes === 60) return '1 hour';
    if (minutes === 90) return '1.5 hours';
    if (minutes === 120) return '2 hours';
    return `${minutes} min`;
  }

  /**
   * Get day name from day index
   * @param dayIndex - Day of week (0-6, Monday=0)
   * @returns Day name
   */
  getDayName(dayIndex: number): string {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    return days[dayIndex] || '';
  }
}

// Export singleton instance
const proBookingService = new ProBookingService();
export default proBookingService;
